import type { PostHog } from "@posthog/react";

export type CoverageBetaImageRole = "query" | "reference" | "thumbnail";

/**
 * Emit a `cb_image_load` PostHog event with load metrics for a coverage-beta
 * image, read from the Performance Resource Timing entry for its URL.
 *
 * Cross-origin caveat: the images are served from GCS (a different origin)
 * without a `Timing-Allow-Origin` header, so the browser zeroes out `ttfb_ms`
 * and the byte sizes for them — those come back null here. Total `duration_ms`
 * is always available. Set `Timing-Allow-Origin` on the image responses (infra)
 * to unlock TTFB / transfer bytes.
 */
export function captureImageLoad(
  posthog: PostHog | undefined,
  role: CoverageBetaImageRole,
  url: string,
  /** Whether the gallery served the optimized rendition (vs. the full-res original). */
  optimized?: boolean,
): void {
  if (!posthog || !url) return;

  let entry: PerformanceResourceTiming | undefined;
  try {
    const entries = performance.getEntriesByName(
      url,
      "resource",
    ) as PerformanceResourceTiming[];
    // The most recent fetch of this URL.
    entry = entries[entries.length - 1];
  } catch {
    entry = undefined;
  }

  // responseStart/requestStart are 0 for opaque cross-origin resources.
  const ttfbMs =
    entry && entry.responseStart > 0 && entry.requestStart > 0
      ? Math.round(entry.responseStart - entry.requestStart)
      : null;

  posthog.capture("cb_image_load", {
    role,
    // Did the gallery serve the optimized rendition, or fall back to full-res?
    optimized: optimized ?? null,
    // Total request→response-end. Available even cross-origin.
    duration_ms: entry ? Math.round(entry.duration) : null,
    ttfb_ms: ttfbMs,
    // Bytes over the wire: 0 for opaque cross-origin OR a cache hit; >0 = real
    // network transfer. Null when no timing entry was found.
    transfer_bytes: entry?.transferSize ?? null,
    encoded_bytes: entry?.encodedBodySize ?? null,
    decoded_bytes: entry?.decodedBodySize ?? null,
    // Cheap cache hint (opaque cross-origin can't report transferSize reliably).
    likely_cached: entry ? entry.duration < 10 : null,
  });
}
